import React, { useCallback, useState, useEffect } from 'react';
import { APIError, useAxios } from '../../core/_axios';
import { TemplateCrop, ServiceSignature } from '../../core/_models';
import { CroppableItems, ManageCropFormCropper } from '../../components/cropper/ManageCropFormCropper';
import { managementUpdateCropValuesByUniqueIdentifier, managementGetCropSignatures, managementSetCropSelectedSignature } from '../../core/_requests';

export default function ManageCropForm({
    crop,
    setCrop: setCrop
}: {
    crop: TemplateCrop,
    setCrop: (crop: TemplateCrop) => void
}) {
    const axios = useAxios();

    const [saving, setSaving] = useState(false);
    const [errorSaving, setErrorSaving] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
    
    const [signatures, setSignatures] = useState<ServiceSignature[]>([]);
    const [totalSignatures, setTotalSignatures] = useState(0);
    const [currentSignatureIndex, setCurrentSignatureIndex] = useState(0);
    const [loadingSignatures, setLoadingSignatures] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage] = useState(100); // Load 100 at a time
    const [allSignatures, setAllSignatures] = useState<ServiceSignature[]>([]);

    const [currentCropValues, setCurrentCropValues] = useState<CroppableItems>(crop.public_data.crop_values || {
        printed_name: null,
        relationship_to_deceased: null,
        email_address: null,
        phone_number: null,
        mailing_address_line_1: null,
        mailing_address_line_2: null,
        signature_image: null,
    }) // Avoids an infinite loop

    const onFormSubmitted = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (saving) {
            return;
        }
        setErrorSaving('');
        setSaving(true);
        setSaveSuccess(null);
        try {
            const updatedCrop = await managementUpdateCropValuesByUniqueIdentifier(axios,
                crop.unique_identifier,
                {
                    crop_values: currentCropValues
                });
            setCrop(updatedCrop);
            setSaveSuccess('Successfully updated crop.');
        } catch (exception) {
            console.error(exception);
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.';
            setErrorSaving(errorMessage);
        } finally {
            setSaving(false);
        }
    }, [saving, axios, crop.unique_identifier, currentCropValues, setCrop]);

    // Load signatures on component mount or when crop changes
    useEffect(() => {
        loadSignatures();
    }, [crop.unique_identifier]);
    
    // Update the current index when crop's latest_signature changes
    useEffect(() => {
        if (crop.latest_signature && signatures.length > 0) {
            const newIndex = signatures.findIndex((sig: ServiceSignature) => 
                sig.unique_identifier === crop.latest_signature?.unique_identifier
            );
            if (newIndex !== -1 && newIndex !== currentSignatureIndex) {
                setCurrentSignatureIndex(newIndex);
            }
        }
    }, [crop.latest_signature?.unique_identifier, signatures]);

    const loadSignatures = useCallback(async () => {
        setLoadingSignatures(true);
        try {
            // Load first page of signatures
            const result = await managementGetCropSignatures(axios, crop.unique_identifier, 1, perPage);
            setSignatures(result.signatures);
            setAllSignatures(result.signatures);
            setTotalSignatures(result.totalCount);
            
            // Find the index of the currently selected signature
            const selectedIndex = result.signatures.findIndex((sig: ServiceSignature) => 
                crop.latest_signature && sig.unique_identifier === crop.latest_signature.unique_identifier
            );
            if (selectedIndex !== -1) {
                setCurrentSignatureIndex(selectedIndex);
            }
            
            // If there are more pages, load them in the background
            if (result.totalPages > 1) {
                loadRemainingPages(result.totalPages);
            }
        } catch (error) {
            console.error('Failed to load signatures:', error);
        } finally {
            setLoadingSignatures(false);
        }
    }, [axios, crop.unique_identifier, crop.latest_signature, perPage]);

    const loadRemainingPages = async (totalPages: number) => {
        const allSigs = [...allSignatures];
        for (let page = 2; page <= totalPages; page++) {
            try {
                const result = await managementGetCropSignatures(axios, crop.unique_identifier, page, perPage);
                allSigs.push(...result.signatures);
            } catch (error) {
                console.error(`Failed to load page ${page}:`, error);
            }
        }
        setAllSignatures(allSigs);
        setSignatures(allSigs);
    };

    const handleSetAsExample = useCallback(async () => {
        const currentSignature = signatures[currentSignatureIndex];
        if (!currentSignature) return;
        
        try {
            setSaving(true);
            setErrorSaving(null);
            const updatedCrop = await managementSetCropSelectedSignature(
                axios, 
                crop.unique_identifier, 
                currentSignature.unique_identifier
            );
            // Update the crop with the new selected signature
            setCrop(updatedCrop);
            setSaveSuccess('Successfully set as example signature.');
            
            // No need to reload signatures, just stay on current index
            // The updated crop will have the new latest_signature
        } catch (error) {
            console.error('Failed to set example signature:', error);
            const errorMessage = error instanceof APIError ? error.message : 'Failed to set example signature';
            setErrorSaving(errorMessage);
        } finally {
            setSaving(false);
        }
    }, [axios, crop.unique_identifier, signatures, currentSignatureIndex, setCrop]);

    // Get the current signature to display
    const displaySignature = signatures[currentSignatureIndex] || crop.latest_signature;
    
    return <div className="">
        <div className="col-span-2">
            <div className="flex flex-col gap-5 lg:gap-7.5">
                <form className="card" onSubmit={onFormSubmitted}>
                    <div className="card-header">
                        <h3 className="card-title">
                            {`Manage Crop (${crop.specific_resolution})`}
                        </h3>
                        {totalSignatures > 1 && (
                            <div className="flex items-center gap-2">
                                {displaySignature && crop.latest_signature && 
                                 displaySignature.unique_identifier !== crop.latest_signature.unique_identifier && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        onClick={handleSetAsExample}
                                        disabled={saving}
                                    >
                                        Set as Example
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-sm btn-light"
                                    onClick={() => setCurrentSignatureIndex(Math.max(0, currentSignatureIndex - 1))}
                                    disabled={currentSignatureIndex === 0 || loadingSignatures}
                                >
                                    ← Previous
                                </button>
                                <span className="text-sm text-gray-600">
                                    {currentSignatureIndex + 1} of {totalSignatures}
                                    {displaySignature && crop.latest_signature && 
                                     displaySignature.unique_identifier === crop.latest_signature.unique_identifier && 
                                     ' (Current)'}
                                </span>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-light"
                                    onClick={() => setCurrentSignatureIndex(Math.min(totalSignatures - 1, currentSignatureIndex + 1))}
                                    disabled={currentSignatureIndex >= signatures.length - 1 || loadingSignatures}
                                >
                                    Next →
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="card-body grid gap-7.5 py-5 lg:py-7.5">
                        <div className="flex flex-col gap-4">
                            <ManageCropFormCropper
                                crop={{...crop, latest_signature: displaySignature}}
                                setCrop={setCrop}
                                currentCropValues={currentCropValues}
                                setCurrentCropValues={setCurrentCropValues}
                            />
                        </div>
                    </div>
                    <div className="card-footer py-3.5 flex flex-col lg:flex-row items-center justify-between">
                        <div>
                            {errorSaving && <div className="text-red-500 text-sm font-medium">{errorSaving}</div>}
                            {saveSuccess && <div className="text-green-500 text-sm font-medium">{saveSuccess}</div>}
                        </div>
                        <div>
                            <button
                                type="submit"
                                className="btn btn-sm btn-primary"
                                disabled={saving}
                            >
                                {!saving ? `Update Crop` : `Updating Crop...`}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
}